import React from "react";
import { CheckIcon } from "@jq-icons/Check";
import { CloseIcon } from "@jq-icons/Close";
import styles from "./password-feedback.module.scss";
import FlexItem from "./../../../../02-atoms/06-layout/03-flex/02-flex-item";
import Text from "./../../../../02-atoms/13-text/03-text";
import FlexItems from "./../../../../02-atoms/06-layout/03-flex";
import Grid from "./../../../../02-atoms/06-layout/04-grid";

export const PASSWORD_MIN_LENGTH = 12;

type PasswordStrength = "weak" | "medium" | "strong";

export interface PasswordFeedbackState {
  /**
   * Whether the password meets the configured minimum character length.
   */
  minLengthValid: boolean;
  /**
   * Whether the password contains at least one letter.
   */
  lettersValid: boolean;
  /**
   * Whether the password contains at least one number.
   */
  numbersValid: boolean;
  /**
   * Whether all configured password requirements are met.
   */
  isValid: boolean;
  /**
   * Human-friendly strength level derived from the fulfilled requirements.
   */
  strength: PasswordStrength;
}

export interface PasswordFeedbackMessages {
  /**
   * Message shown when the password meets all requirements.
   */
  valid: string;
  /**
   * Message shown when the password does not meet all requirements.
   */
  invalid: string;
  /**
   * Label for a weak password.
   */
  weak: string;
  /**
   * Label for a medium-strength password.
   */
  medium: string;
  /**
   * Label for a strong password.
   */
  strong: string;
  /**
   * Label for the minimum length requirement.
   */
  minLength: string;
  /**
   * Label for the letters requirement.
   */
  letters: string;
  /**
   * Label for the numbers requirement.
   */
  numbers: string;
}

const defaultMessages: PasswordFeedbackMessages = {
  valid: "Password is valid.",
  invalid: "Password does not meet requirements yet.",
  weak: "Weak",
  medium: "Medium",
  strong: "Strong",
  minLength: `At least ${PASSWORD_MIN_LENGTH} characters`,
  letters: "Contains letters",
  numbers: "Contains numbers",
};

/**
 * Evaluates the given password against the configured requirements and returns an object containing the validation results and derived strength level.
 *
 * @param password
 *  The password string to evaluate.
 * @param minLength
 *  The minimum required length for the password. Defaults to `PASSWORD_MIN_LENGTH`.
 *
 * @returns
 *  An object containing boolean flags for each requirement, an overall validity flag, and a strength level.
 */
export const getPasswordFeedbackState = (
  password: string,
  minLength = PASSWORD_MIN_LENGTH,
): PasswordFeedbackState => {
  const minLengthValid = password.length >= minLength;
  const lettersValid = /[A-Za-z]/.test(password);
  const numbersValid = /\d/.test(password);
  // Calculate strength based on how many requirements are met.
  const score = [minLengthValid, lettersValid, numbersValid].filter(
    Boolean,
  ).length;
  const isValid = score === 3;

  let strength: PasswordStrength = "weak";
  if (score === 2) {
    strength = "medium";
  }
  if (score === 3) {
    strength = "strong";
  }

  return {
    minLengthValid,
    lettersValid,
    numbersValid,
    isValid,
    strength,
  };
};

export const isPasswordPolicyValid = (
  password: string,
  minLength = PASSWORD_MIN_LENGTH,
): boolean => getPasswordFeedbackState(password, minLength).isValid;

interface PasswordFeedbackProps {
  /**
   * The password string to evaluate and provide feedback for.
   */
  password: string;
  /**
   * Optional custom messages for the feedback component. Any missing messages will fall back to defaults.
   */
  messages?: Partial<PasswordFeedbackMessages>;
  /**
   * The minimum required length for the password. Defaults to `PASSWORD_MIN_LENGTH`.
   */
  minLength?: number;
}

export const PasswordFeedback = ({
  password,
  messages = {},
  minLength = PASSWORD_MIN_LENGTH,
}: PasswordFeedbackProps) => {
  if (!password) {
    return null;
  }

  const feedback = getPasswordFeedbackState(password, minLength);
  // Merge default messages with any provided
  // custom messages (because of translations).
  const resolvedMessages = { ...defaultMessages, ...messages };
  const strengthLabelByKey: Record<PasswordStrength, string> = {
    weak: resolvedMessages.weak,
    medium: resolvedMessages.medium,
    strong: resolvedMessages.strong,
  };
  const criteria = [
    {
      id: "min-length",
      isMet: feedback.minLengthValid,
      label: resolvedMessages.minLength,
    },
    {
      id: "letters",
      isMet: feedback.lettersValid,
      label: resolvedMessages.letters,
    },
    {
      id: "numbers",
      isMet: feedback.numbersValid,
      label: resolvedMessages.numbers,
    },
  ];

  return (
    <FlexItems gap={1} flexDirection="column" data-test-id="password-feedback">
      <Text
        marginTop={0}
        marginBottom={0}
        fontSize={1}
        color={feedback.isValid ? "success" : "danger"}
        className={styles.status}
      >
        {feedback.isValid ? resolvedMessages.valid : resolvedMessages.invalid}
      </Text>
      <Grid columns="1fr auto" gap={4} alignItems="center">
        <div className={styles.bar} aria-hidden="true">
          <div className={`${styles.fill} ${styles[feedback.strength]}`} />
        </div>
        <Text marginTop={0} marginBottom={0} fontSize={1}>
          {strengthLabelByKey[feedback.strength]}
        </Text>
      </Grid>
      <ul className={styles.criteriaList}>
        {criteria.map(({ id, isMet, label }) => (
          <li className={styles.criteriaItem} key={id}>
            <FlexItems alignItems="center" flexWrap="nowrap" gap={2}>
              <FlexItem className={styles.criteriaIcon}>
                {isMet ? (
                  <CheckIcon
                    title={label}
                    width="14"
                    height="14"
                    color="success"
                  />
                ) : (
                  <CloseIcon
                    title={label}
                    width="14"
                    height="14"
                    color="gray6"
                  />
                )}
              </FlexItem>
              <FlexItem>
                <Text
                  marginTop={0}
                  marginBottom={0}
                  fontSize={1}
                  color={isMet ? "success" : "gray6"}
                >
                  {label}
                </Text>
              </FlexItem>
            </FlexItems>
          </li>
        ))}
      </ul>
    </FlexItems>
  );
};

export default PasswordFeedback;
